<script lang="ts">
	import { Button, Container, Select, Stack, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import {
		MAX_WEEK_PATTERNS,
		dayModelSelectOptionForStorageIndex,
		handleSelectChange,
		type TariffTables,
	} from '../helpers/daisy_em_tariff_helpers';

	export let tariffTables: TariffTables;
	export let weekdayEntries: { label: string; dataIndex: number }[];

	$: activeDayIndices = tariffTables.days.reduce<number[]>((indices, pattern, dayIdx) => {
		if (pattern.some(Boolean)) indices.push(dayIdx);
		return indices;
	}, []);

	$: dayModelOptions = activeDayIndices.map((_, i) => ({
		value: `D${i + 1}`,
		title: $format('daisy_em_tariff_day_model_option', {
			values: { n: i + 1 },
			default: `Day model ${i + 1}`,
		}),
	}));

	/** Appends a new week pattern with all days mapped to day pattern 0 (max 3). */
	const addWeekPattern = () => {
		if (tariffTables.weeks.length >= MAX_WEEK_PATTERNS) return;
		tariffTables.weeks = [...tariffTables.weeks, Array(7).fill(0)];
		tariffTables = { ...tariffTables };
	};

	/**
	 * Removes a week pattern by index and clamps season references to the new length.
	 * @param weekIdx - Index of the week pattern to remove.
	 */
	// const removeWeekPattern = (weekIdx: number) => {
	// 	if (tariffTables.weeks.length <= 1) return;
	// 	tariffTables.weeks = tariffTables.weeks.filter((_, i) => i !== weekIdx);
	// 	tariffTables.seasons = [
	// 		Math.min(tariffTables.seasons[0], tariffTables.weeks.length - 1),
	// 		Math.min(tariffTables.seasons[1], tariffTables.weeks.length - 1),
	// 	];
	// 	tariffTables = { ...tariffTables };
	// };EVENTUALLY: THIS IS POSSIBLE TO WORK for now it isn't

	/**
	 * Handles the Select change event for a day-of-week assignment within a week pattern.
	 * @param weekIdx - Week pattern index.
	 * @param dayOfWeek - Day-of-week index (0 = Sunday … 6 = Saturday).
	 * @param e - Change event from the Select component.
	 */
	const handleWeekDayChange = (weekIdx: number, dayOfWeek: number, e: CustomEvent) => {
		const compressedIdx = activeDayIndices.map((_, i) => `D${i + 1}`).indexOf(handleSelectChange(e));
		tariffTables.weeks[weekIdx][dayOfWeek] =
			activeDayIndices[compressedIdx] ?? activeDayIndices[0] ?? 0;
		tariffTables = { ...tariffTables };
	};
</script>

<Text styleType="h2" class="mb-s mt-m">
	{$format('daisy_em_tariff_week_patterns', { default: 'Week Patterns' })}
</Text>
{#each tariffTables.weeks as week, weekIdx}
	<Container type="borderSolid" backgroundColor="color-base" class="mb-s">
		<Text styleType="body-small" class="text-bold mb-s">
			{$format('daisy_em_tariff_week_pattern_label', {
				values: { n: weekIdx + 1 },
				default: `Week Pattern ${weekIdx + 1}`,
			})}
		</Text>
		{#each weekdayEntries as { label, dataIndex }}
			<Stack direction="column" align="between" gap="xs" class="d-flex align-items-center mb-s">
				<Text styleType="body" class="text-color-muted width-s">{label}</Text>
				<Select
					id={`week-${weekIdx}-${dataIndex}`}
					aria-label={$format('daisy_em_tariff_weekday_day_model', {
						values: { week: weekIdx + 1, weekday: label },
						default: `Day model for week ${weekIdx + 1}, ${label}`,
					})}
					size="m"
					width="full"
					icon="fas fa-calendar-day"
					value={dayModelSelectOptionForStorageIndex(
						dayModelOptions,
						activeDayIndices,
						week[dataIndex],
					)}
					options={dayModelOptions}
					on:change={(e) => handleWeekDayChange(weekIdx, dataIndex, e)}
				/>
			</Stack>
		{/each}
	</Container>
{/each}
{#if tariffTables.weeks.length < MAX_WEEK_PATTERNS}
	<Button size="m" styleType="secondary" on:click={addWeekPattern} icon="fas fa-plus">
		{$format('daisy_em_tariff_add_week_pattern', { default: 'Add week pattern' })}
	</Button>
{/if}
